/**
 * Anlage eines Customer-Workspaces. Der erste Administrator wird EINGELADEN, nicht mit Passwort
 * angelegt — die Plattform vergibt keine Kunden-Zugangsdaten.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
} from '@/shared/components';
import type { CreateWorkspaceRequest } from '@/shared/api';
import { REPLY_ADDRESS_HINT } from '@/shared/lib/replyAddress';
import { currencyOptions, timeZoneOptions } from '@/shared/lib/workspaceValueOptions';
import { useContactPrefill } from './useContactPrefill';
import { WorkspaceInitialAdminFields } from './WorkspaceInitialAdminFields';
import {
  emptyWorkspaceCreateValues,
  toCreateRequest,
  workspaceCreateSchema,
  type WorkspaceCreateValues,
} from './workspaceCreateModel';

export function WorkspaceCreateDialog({
  open,
  onOpenChange,
  loading,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading?: boolean;
  onSubmit: (values: CreateWorkspaceRequest) => void;
}) {
  const { t } = useTranslation('workspaces');
  const form = useForm<WorkspaceCreateValues>({
    resolver: zodResolver(workspaceCreateSchema),
    defaultValues: emptyWorkspaceCreateValues,
  });
  const { register, handleSubmit, reset } = form;
  const { errors } = form.formState;
  const contact = useContactPrefill(form, open);

  useEffect(() => {
    if (open) reset(emptyWorkspaceCreateValues);
  }, [open, reset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Workspace anlegen</DialogTitle>
          <DialogDescription>{t('createDescription')}</DialogDescription>
        </DialogHeader>
        {/* Die Form ist die Spalte des Dialogs: nur so scrollt der Inhalt und der Fuß mit
            „Anlegen" bleibt auf kleinen Fenstern sichtbar (§8). */}
        <form
          onSubmit={handleSubmit((values) => onSubmit(toCreateRequest(values)))}
          className="flex min-h-0 flex-1 flex-col"
        >
          <DialogBody className="space-y-4">
            <FormField label="Name" error={errors.name?.message}>
              <Input autoComplete="off" {...register('name')} />
            </FormField>
            <FormField
              label="Unternehmenskürzel"
              error={errors.slug?.message}
              hint="Damit meldet sich der Kunde an. Nur Kleinbuchstaben, Ziffern und Bindestriche."
            >
              <Input autoComplete="off" {...register('slug')} />
            </FormField>
            <WorkspaceInitialAdminFields form={form} />
            {/* Auf 360 px stünden zwei Felder nebeneinander bei je rund 140 px — erst ab der
                schmalen Tabletbreite ist die Doppelspalte lesbar. */}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Zeitzone" error={errors.timeZone?.message}>
                <Select {...register('timeZone')}>
                  {timeZoneOptions().map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Währung" error={errors.defaultCurrency?.message}>
                <Select {...register('defaultCurrency')}>
                  {currencyOptions().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>
            <FormField
              label="Kontakt-E-Mail"
              error={errors.contactEmail?.message}
              hint={`${REPLY_ADDRESS_HINT} ${t('contactHint')}`}
            >
              <Input
                type="email"
                autoComplete="off"
                {...register('contactEmail', { onChange: contact.markEdited })}
              />
            </FormField>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              className="min-h-11"
              onClick={() => onOpenChange(false)}
            >
              Abbrechen
            </Button>
            {/* Die primäre Aktion war eine Ghost-Schaltfläche und damit optisch gleichrangig
                mit „Abbrechen" (§11). */}
            <Button type="submit" variant="brand" className="min-h-11" loading={loading}>
              {t(form.watch('inviteAdmin') ? 'createAndInvite' : 'createOnly')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
